"use client";

import { Phone, ShieldCheck, FileCheck, Truck, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const trustBadges = [{
  id: "licensed",
  title: "Licensed & Insured",
  subtext: "$10M Liability & WCB Coverage"
}, {
  id: "warranty",
  title: "Lifetime Warranty",
  subtext: "Limited material warranty backed by manufacturer"
}, {
  id: "serving",
  title: "Calgary & Area",
  subtext: "Airdrie, Cochrane, Okotoks"
}, {
  id: "rating",
  title: "5.0 Star Rating",
  subtext: "Top-Rated Local Contractor"
}];
export default function Exterior() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.15
  });
  const {
    ref: ledeRef,
    isVisible: ledeVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: trustRef,
    isVisible: trustVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="14567" id="elevated-exterior-solutions" className="dark bg-background text-foreground py-16 md:py-40 px-6 md:px-10 lg:px-16">
      <div className="mx-auto max-w-[1240px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <h1 ref={headlineRef} className="font-default font-bold leading-[1.05] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.75rem, 5.8vw, 5.25rem)",
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 700ms ease-out, transform 700ms ease-out"
          }}>
              Metal Roofing & Siding Company{" "}
              <span className="italic" style={{
              color: "var(--chart-1)",
              display: "inline-block",
              opacity: headlineVisible ? 1 : 0,
              transform: headlineVisible ? "translateY(0)" : "translateY(6px)",
              transition: "opacity 600ms ease-out 200ms, transform 600ms ease-out 200ms"
            }}>
                in Calgary
              </span>
            </h1>

            <p ref={ledeRef} className="font-default text-muted-foreground mt-8 max-w-[560px] leading-[1.65]" style={{
            fontSize: "clamp(1.0625rem, 1.4vw, 1.25rem)",
            opacity: ledeVisible ? 1 : 0,
            transform: ledeVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 700ms ease-out 120ms, transform 700ms ease-out 120ms"
          }}>
              Professional metal roofing and siding installation & replacement for residential and commercial properties across Calgary and surrounding areas.
            </p>

            <div ref={ctaRef} className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mt-10" style={{
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 700ms ease-out 240ms, transform 700ms ease-out 240ms"
          }}>
              <Button asChild size="lg" className="rounded-lg h-auto px-7 py-3.5 text-base font-medium font-default shadow-md hover:opacity-90 transition-all duration-300 ease-out" style={{
              backgroundColor: "var(--chart-1)",
              color: "var(--background)"
            }}>
                <Link to="#start-your-transformation">Get a Free Estimate</Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-lg h-auto px-6 py-3.5 text-base font-medium font-default shadow-md border hover:bg-white/10 transition-all duration-300 ease-out" style={{
              borderColor: "var(--chart-1)",
              color: "var(--chart-1)"
            }}>
                <Link to="tel:+15878884223">
                  <Phone className="size-4" />
                  Call{"  "}(587) 888-4223
                </Link>
              </Button>
            </div>

            <div ref={trustRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-[560px] border-t border-white/10 pt-8 mt-10" style={{
            opacity: trustVisible ? 1 : 0,
            transform: trustVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 700ms ease-out 320ms, transform 700ms ease-out 320ms"
          }}>
              {trustBadges.map((badge, i) => {
              const IconComponent = i === 0 ? ShieldCheck : i === 1 ? FileCheck : i === 2 ? Truck : Star;
              return <div key={badge.id} data-index={i} className="flex items-start gap-3">
                    <IconComponent className="size-5 shrink-0 mt-0.5" style={{
                  color: "var(--chart-1)"
                }} />
                    <div>
                      <div className="font-default font-semibold text-sm text-foreground">{badge.title}</div>
                      <div className="font-default text-xs text-muted-foreground mt-0.5">{badge.subtext}</div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div ref={imgRef} className="lg:col-span-5 order-1 lg:order-2" style={{
          opacity: imgVisible ? 1 : 0,
          transform: imgVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 800ms ease-out, transform 800ms ease-out"
        }}>
            <div className="overflow-hidden rounded-lg">
              <AspectRatio ratio={4 / 5}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/premium_metal_cladding_project.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/premium_metal_cladding_project.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/premium_metal_cladding_project.webp-wvc-sizes" alt="Modern luxury home featuring architectural metal cladding and standing seam roofing" loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
              </AspectRatio>
            </div>
          </div>
        </div>
      </div>
    </section>;
}